/**
 * Engine for the `userAndGroup` field control: turns the org directory
 * (`UserContext.users` / `UserContext.businessGroups`) into Select or Cascader
 * options and translates control events back into a `UserAndGroupValue`.
 * Consumed by `UserAndGroupUI`, which `ControlUI` feeds from `FieldType.props`.
 * Invariants: options may be narrowed by `restrictToBusinessGroupIds`, but the
 * name maps always cover the whole directory so already-stored selections still
 * render; `selectOptions` therefore appends one option per selected id the
 * offered set omits — removable, never `disabled` — so the Select never shows a
 * raw id; `userNameMap` is the only place a user's label is derived, falling
 * `name → email → docId` as the group admin panels do, because `users/` docs
 * created without a display name would otherwise surface as a bare uid; every
 * change preserves the `weightPercentage` of surviving selections (keyed by
 * selection id); `maxSelections` only ever lowers the global cap; in the flat
 * `usersAndGroups` mode a selected id's `type` is resolved by group-map lookup
 * rather than assumed from the mode; `weightTotal` is non-null only when both
 * `enableWeights` and `enforceWeightTotal` are on, and its target defaults to
 * 100; selected options carry their `weightPercentage` in the option label so
 * the Select's own tags show the percent.
 */
import { useContext, useMemo, useCallback } from 'react';
import {
    UserAndGroupValue,
    UserAndGroupSelection,
    UserAndGroupFieldTypeProps,
    BusinessGroup,
} from '#types';
import { MAX_USER_AND_GROUP_SELECTIONS } from '#types/System.Parameters.types';
import { UserContext } from '../../../../providers/UserContext';

interface UseUserAndGroupUIProps extends UserAndGroupFieldTypeProps {
    value: string | UserAndGroupValue | null | undefined;
    onChange: (value: UserAndGroupValue) => void;
}

interface CascaderOption {
    value: string;
    label: string;
    disabled?: boolean;
    children?: CascaderOption[];
}

type UserDoc = { docId: string; name?: string; email?: string; businessGroups?: string[] };

export const useUserAndGroupUI = ({
    value: rawValue,
    onChange,
    selectionMode = 'usersOnly',
    multiSelect = false,
    restrictToBusinessGroupIds,
    maxSelections,
    enableWeights = false,
    enforceWeightTotal = false,
    targetWeightTotal,
}: UseUserAndGroupUIProps) => {
    const { users: rawUsers, businessGroups: rawGroups } = useContext(UserContext);

    const users = useMemo(
        (): UserDoc[] =>
            rawUsers
                .filter((u): u is typeof u & { docId: string } => !!u.docId)
                .map((u) => ({
                    docId: u.docId!,
                    name: (u as any).name as string | undefined,
                    email: (u as any).email as string | undefined,
                    businessGroups: (u as any).businessGroups as string[] | undefined,
                })),
        [rawUsers],
    );

    const groups = useMemo(
        (): (BusinessGroup & { docId: string })[] =>
            rawGroups.filter((g): g is BusinessGroup & { docId: string } => !!g.docId),
        [rawGroups],
    );

    const normalizedValue = useMemo((): UserAndGroupValue => {
        if (!rawValue) return { formatted: '', components: { selections: [] } };
        if (typeof rawValue === 'string') {
            return { formatted: rawValue, components: { selections: [] } };
        }
        return rawValue;
    }, [rawValue]);

    const selections = useMemo(
        () => normalizedValue.components?.selections ?? [],
        [normalizedValue],
    );

    const userNameMap = useMemo(() => {
        const map = new Map<string, string>();
        for (const u of users) {
            map.set(u.docId, u.name || u.email || u.docId);
        }
        return map;
    }, [users]);

    const groupNameMap = useMemo(() => {
        const map = new Map<string, string>();
        for (const g of groups) {
            map.set(g.docId, g.name);
        }
        return map;
    }, [groups]);

    const userGroupMembership = useMemo(() => {
        const map = new Map<string, string[]>();
        for (const u of users) {
            map.set(u.docId, u.businessGroups ?? []);
        }
        return map;
    }, [users]);

    const restrictionSet = useMemo(
        () =>
            restrictToBusinessGroupIds && restrictToBusinessGroupIds.length > 0
                ? new Set(restrictToBusinessGroupIds)
                : undefined,
        [restrictToBusinessGroupIds],
    );

    const offeredUsers = useMemo(
        () =>
            restrictionSet
                ? users.filter((u) =>
                    (userGroupMembership.get(u.docId) ?? []).some((gId) => restrictionSet.has(gId)),
                )
                : users,
        [restrictionSet, users, userGroupMembership],
    );

    const offeredGroups = useMemo(
        () => (restrictionSet ? groups.filter((g) => restrictionSet.has(g.docId)) : groups),
        [restrictionSet, groups],
    );

    const selectionCap = Math.min(
        maxSelections ?? MAX_USER_AND_GROUP_SELECTIONS,
        MAX_USER_AND_GROUP_SELECTIONS,
    );

    const isFlatSelectMode =
        selectionMode === 'usersOnly'
        || selectionMode === 'groupsOnly'
        || selectionMode === 'usersAndGroups';

    const typeForId = useCallback(
        (id: string): 'user' | 'group' => {
            if (selectionMode === 'groupsOnly') return 'group';
            if (selectionMode === 'usersOnly') return 'user';
            return groupNameMap.has(id) ? 'group' : 'user';
        },
        [selectionMode, groupNameMap],
    );

    const selectOptions = useMemo(() => {
        if (!isFlatSelectMode) return [];

        const offered = [
            ...(selectionMode === 'usersOnly'
                ? []
                : offeredGroups.map((g) => ({ value: g.docId, label: g.name }))),
            ...(selectionMode === 'groupsOnly'
                ? []
                : offeredUsers.map((u) => ({
                    value: u.docId,
                    label: userNameMap.get(u.docId) ?? u.docId,
                }))),
        ];

        const offeredIds = new Set(offered.map((o) => o.value));
        const storedOutsideOffer = selections
            .filter((s, i) => !offeredIds.has(s.id) && selections.findIndex((o) => o.id === s.id) === i)
            .map((s) => ({
                value: s.id,
                label: (s.type === 'group' ? groupNameMap : userNameMap).get(s.id) ?? s.name ?? s.id,
            }));

        const weights = new Map(
            selections
                .filter((s) => s.weightPercentage !== undefined)
                .map((s) => [s.id, s.weightPercentage] as const),
        );

        return [...offered, ...storedOutsideOffer].map((o) =>
            weights.has(o.value) ? { ...o, label: `${o.label} (${weights.get(o.value)}%)` } : o,
        );
    }, [isFlatSelectMode, selectionMode, offeredUsers, offeredGroups, selections, userNameMap, groupNameMap]);

    const cascaderOptions = useMemo((): CascaderOption[] => {
        if (selectionMode !== 'cascadeUsersOnly' && selectionMode !== 'cascadeUsersAndGroups') {
            return [];
        }

        const groupedUsers = new Set<string>();
        const options: CascaderOption[] = offeredGroups
            .map((g) => {
                const children: CascaderOption[] = offeredUsers
                    .filter((u) => {
                        const memberGroups = userGroupMembership.get(u.docId) ?? [];
                        return memberGroups.includes(g.docId);
                    })
                    .map((u) => {
                        groupedUsers.add(u.docId);
                        return {
                            value: u.docId,
                            label: userNameMap.get(u.docId) ?? u.docId,
                        };
                    });
                return {
                    value: g.docId,
                    label: g.name,
                    children,
                };
            })
            .filter((opt) => opt.children && opt.children.length > 0);

        const ungroupedUsers = offeredUsers.filter((u) => !groupedUsers.has(u.docId));
        if (ungroupedUsers.length > 0) {
            options.push({
                value: '__ungrouped__',
                label: 'Ungrouped',
                disabled: true,
                children: ungroupedUsers.map((u) => ({
                    value: u.docId,
                    label: userNameMap.get(u.docId) ?? u.docId,
                })),
            });
        }

        return options;
    }, [selectionMode, offeredGroups, offeredUsers, userGroupMembership, userNameMap]);

    const isCascaderMode = selectionMode === 'cascadeUsersOnly' || selectionMode === 'cascadeUsersAndGroups';

    const selectValue = useMemo(() => {
        if (isCascaderMode) return undefined;
        const ids = selections.map((s) => s.id);
        return multiSelect ? ids : ids[0] ?? undefined;
    }, [isCascaderMode, selections, multiSelect]);

    const cascaderValue = useMemo((): string[] | string[][] | undefined => {
        if (!isCascaderMode) return undefined;

        const paths = selections.map((s): string[] => {
            if (s.type === 'group') return [s.id];
            const memberGroups = userGroupMembership.get(s.id) ?? [];
            const firstGroup = memberGroups.find((gId) =>
                cascaderOptions.some((opt) => opt.value === gId),
            );
            return [firstGroup ?? '__ungrouped__', s.id];
        });

        return multiSelect ? paths : paths[0];
    }, [isCascaderMode, selections, userGroupMembership, cascaderOptions, multiSelect]);

    const weightById = useMemo(() => {
        const map = new Map<string, number>();
        for (const s of selections) {
            if (s.weightPercentage !== undefined) map.set(s.id, s.weightPercentage);
        }
        return map;
    }, [selections]);

    const buildSelection = useCallback(
        (type: 'user' | 'group', id: string): UserAndGroupSelection => {
            const name = type === 'group' ? groupNameMap.get(id) : userNameMap.get(id);
            const weight = weightById.get(id);
            return weight === undefined
                ? { type, id, name }
                : { type, id, name, weightPercentage: weight };
        },
        [groupNameMap, userNameMap, weightById],
    );

    const buildFormatted = useCallback(
        (sels: UserAndGroupSelection[]): string =>
            sels
                .map((s) => {
                    const label = s.type === 'group'
                        ? groupNameMap.get(s.id) ?? s.name ?? s.id
                        : userNameMap.get(s.id) ?? s.name ?? s.id;
                    return s.weightPercentage === undefined
                        ? label
                        : `${label} (${s.weightPercentage}%)`;
                })
                .join(', '),
        [userNameMap, groupNameMap],
    );

    const handleSelectChange = useCallback(
        (selected: string | string[] | undefined) => {
            if (selected === undefined || selected === null) {
                onChange({ formatted: '', components: { selections: [] } });
                return;
            }
            const ids = Array.isArray(selected) ? selected : [selected];
            const capped = ids.slice(0, selectionCap);
            const newSelections = capped.map((id) => buildSelection(typeForId(id), id));
            onChange({
                formatted: buildFormatted(newSelections),
                components: { selections: newSelections },
            });
        },
        [onChange, typeForId, selectionCap, buildSelection, buildFormatted],
    );

    const handleCascaderChange = useCallback(
        (paths: (string | number)[][] | (string | number)[]) => {
            if (!paths || (Array.isArray(paths) && paths.length === 0)) {
                onChange({ formatted: '', components: { selections: [] } });
                return;
            }

            const rawPaths: (string | number)[][] = multiSelect
                ? (paths as (string | number)[][])
                : [paths as (string | number)[]];

            const capped = rawPaths
                .filter((path) => !(path.length === 1 && String(path[0]) === '__ungrouped__'))
                .slice(0, selectionCap);
            const newSelections = capped.map((path) => {
                const strPath = path.map(String);
                return strPath.length === 1
                    ? buildSelection('group', strPath[0])
                    : buildSelection('user', strPath[strPath.length - 1]);
            });

            onChange({
                formatted: buildFormatted(newSelections),
                components: { selections: newSelections },
            });
        },
        [onChange, multiSelect, selectionCap, buildSelection, buildFormatted],
    );

    const handleWeightChange = useCallback(
        (id: string, weight: number | null) => {
            const newSelections = selections.map((s): UserAndGroupSelection => {
                if (s.id !== id) return s;
                if (weight === null) return { type: s.type, id: s.id, name: s.name };
                return { ...s, weightPercentage: Math.min(100, Math.max(0, weight)) };
            });
            onChange({
                formatted: buildFormatted(newSelections),
                components: { selections: newSelections },
            });
        },
        [onChange, selections, buildFormatted],
    );

    const weightTotal = useMemo(() => {
        if (!enableWeights || !enforceWeightTotal) return null;
        const sum = selections.reduce((total, s) => total + (s.weightPercentage ?? 0), 0);
        const target = targetWeightTotal ?? 100;
        return { sum, target, isValid: sum === target };
    }, [enableWeights, enforceWeightTotal, targetWeightTotal, selections]);

    const handleClear = useCallback(() => {
        onChange({ formatted: '', components: { selections: [] } });
    }, [onChange]);

    return {
        selections,
        selectOptions,
        cascaderOptions,
        isCascaderMode,
        selectValue,
        cascaderValue,
        selectionCap,
        weightTotal,
        handleSelectChange,
        handleCascaderChange,
        handleWeightChange,
        handleClear,
        userNameMap,
        groupNameMap,
    };
};
